"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { HomeProduct } from "@/types/home";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import {
  calculateNetTotal,
  cartPriceType,
  formatWithCommasWithDecimal,
  getCurrentQuantity,
  getNDPPriceAndQty,
  handleAddToCart,
  handleRemoveFromCart,
  isInBulkCart,
} from "@/utils/helpers";
import MobilePriceCell from "./MobilePriceCell";
import { AnimatedQuantity, FormattedNumber } from "@/utils/numberFlow";
import { Input } from "@/components/ui/input";
import { CircularProgress } from "@mui/material";
import { cn } from "@/lib/utils";
import { LoaderState } from "@/types/cart";
import { trackEvent } from "@/lib/gtag";
import debounce from "lodash.debounce";

interface Props {
  data: HomeProduct;
}

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

const MobileProductCard: React.FC<Props> = ({ data }) => {
  const dispatch = useAppDispatch();
  const { cart } = useAppSelector((state) => state.cart);
  const { phoneNumber } = useAppSelector((state) => state.auth);
  const { SKU_Details } = useAppSelector((state) => state.home);
  const [open, setOpen] = useState<LoaderState | null>(null);

  const handleOpen = (sku: string, type: string) =>
    setOpen({ sku: sku, type: type });
  const handleClose = () => setOpen(null);

  const redixData = getNDPPriceAndQty(SKU_Details, data?.SKU_ID);
  const currentQuantity = getCurrentQuantity(data?.SKU_ID, cart) || 0;
  const [inputQty, setInputQty] = useState<string>(
    String(currentQuantity) || ""
  );
  const [selected, setSelected] = useState<"ndp" | "sales">(
    cartPriceType(cart, data?.SKU_ID)
  );

  const debouncedUpdateCart = debounce((qty: number) => {
    handleAddToCart(
      dispatch,
      data?.SKU_ID,
      phoneNumber,
      qty,
      selected,
      handleClose
    );
  }, 500);

  const handleInputQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    const availableQty = redixData?.qty ?? 0;
    if (availableQty > Number(value)) {
      setInputQty(value);
    } else {
      setInputQty(String(availableQty));
    }
    const numericValue =
      availableQty > Number(value) ? parseInt(value) : availableQty;
    if (!isNaN(numericValue)) debouncedUpdateCart(numericValue);
  };

  useEffect(() => {
    if (selected === "ndp") {
      setInputQty(currentQuantity.toString());
    }
  }, [selected, currentQuantity]);

  const handleAddCarts = (type: string) => {
    const bundleQty = parseInt(data?.Bundle) || 1;
    const availableQty = redixData?.qty ?? 0;

    let newQty = currentQuantity;

    if (type === "inc") {
      if (availableQty <= currentQuantity) {
        return;
      }

      if (selected === "ndp") {
        if (newQty + 1 <= availableQty) {
          newQty += 1;
        } else {
          return;
        }
      } else if (selected === "sales") {
        const remainder = currentQuantity % bundleQty;
        const increment = remainder === 0 ? bundleQty : bundleQty - remainder;
        if (newQty + increment <= availableQty) {
          newQty += increment;
        } else {
          return;
        }
      } else {
        // default case (e.g., wholesale)
        if (newQty + bundleQty <= availableQty) {
          newQty += bundleQty;
        } else {
          return;
        }
      }
    }

    if (type === "dec") {
      if (selected === "ndp") {
        newQty = Math.max(0, newQty - 1);
      } else if (selected === "sales") {
        const remainder = newQty % bundleQty;
        newQty =
          remainder === 0
            ? Math.max(0, newQty - bundleQty)
            : Math.max(0, newQty - remainder);
      } else {
        newQty = Math.max(0, newQty - bundleQty);
      }
    }

    // Avoid adding 0 quantity
    if (newQty === 0 && type === "dec") {
      return;
    }

    trackEvent({
      action: type === "inc" ? "Increase Quantity" : "Decrease Quantity",
      category: "Product",
      label: data?.SKU_ID,
    });

    handleOpen(data?.SKU_ID, type);

    handleAddToCart(
      dispatch,
      data?.SKU_ID,
      phoneNumber,
      newQty,
      selected,
      handleClose
    );
  };

  const handleRemove = async () => {
    if (!isInBulkCart(cart, data?.SKU_ID)) return;

    await handleOpen(data?.SKU_ID, "refresh");

    trackEvent({
      action: "Remove Product",
      category: "Product",
      label: data?.SKU_ID,
    });

    if (open?.type === "refresh") {
      await gsap.to("#refresh-icon", {
        rotation: 360,
        repeat: -1,
        duration: 1,
        ease: "linear",
      });
    }

    await handleRemoveFromCart(
      dispatch,
      data?.SKU_ID,
      phoneNumber,
      handleClose
    );

    if (open === null) {
      gsap.killTweensOf("#refresh-icon");
      gsap.to("#refresh-icon", { rotation: 0, duration: 0.3 });
    }
  };

  const toTitleCase = (str: string) =>
    str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  return (
    <Card className="w-full p-4 rounded-2xl shadow-lg border border-gray-200">
      <div className="flex gap-4">
        <Image
          src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_PRODUCTS_IMAGE/${data.Image?.[0]}`}
          alt="product"
          width={80}
          height={80}
          className="rounded-md object-contain w-[80px] h-[80px] border border-gray-300"
          unoptimized
        />
        <div className="flex-1">
          <Image
            src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_MANUFACTURE_IMAGE/${data?.manufacture_images}`}
            alt="brand"
            width={60}
            height={20}
            className="object-contain mb-1"
            unoptimized
          />
          <p className="font-semibold text-[15px] text-black line-clamp-2">
            {toTitleCase(data.prod_name?.trim() || "")}
          </p>
          <div className="text-gray-500 text-sm mt-1">
            {data?.Tech_Data?.trim()
              ? toTitleCase(data.Tech_Data)
              : data?.ProductTechAttributes?.filter(Boolean).map(
                  (tech, index, arr) => (
                    <span key={index}>
                      {toTitleCase(tech)}
                      {index !== arr.length - 1 && ", "}
                    </span>
                  )
                )}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-muted-foreground text-sm">
              MRP: <b>₹{formatWithCommasWithDecimal(Number(data.MRP_price))}</b>
            </p>
            <p className="text-muted-foreground text-sm">
              GST: <b>{data.GST}%</b>
            </p>
          </div>
        </div>
      </div>

      <MobilePriceCell
        data={data}
        redixData={redixData}
        selected={selected}
        setSelected={setSelected}
      />

      <div className="w-full flex items-center justify-between mt-4">
        <div className="flex items-center">
          <Button
            className={cn(
              "w-9 h-9 text-lg rounded-none rounded-l-md",
              currentQuantity > 0
                ? "bg-green-600 hover:bg-green-600"
                : "bg-green-300 hover:bg-green-300"
            )}
            onClick={() => handleAddCarts("dec")}
          >
            {open?.sku === data?.SKU_ID && open?.type === "dec" ? (
              <CircularProgress size={12} sx={{ color: "white" }} />
            ) : (
              "-"
            )}
          </Button>

          {selected === "ndp" ? (
            <Input
              value={inputQty}
              onChange={handleInputQtyChange}
              className="text-center text-black-1 text-sm w-12 h-9 bg-gray-100 border-none"
            />
          ) : (
            <div className="w-12 h-9 text-black-1 flex items-center justify-center bg-gray-100 text-sm">
              <AnimatedQuantity
                value={isInBulkCart(cart, data?.SKU_ID) ? currentQuantity : 0}
              />
            </div>
          )}

          <Button
            className={cn(
              "w-9 h-9 text-lg rounded-none rounded-r-md",
              currentQuantity < (redixData?.qty || 0)
                ? "bg-green-600 hover:bg-green-600"
                : "bg-green-300 hover:bg-green-300"
            )}
            onClick={() => handleAddCarts("inc")}
          >
            {open?.sku === data?.SKU_ID && open?.type === "inc" ? (
              <CircularProgress size={12} sx={{ color: "white" }} />
            ) : (
              "+"
            )}
          </Button>

          <Button
            onClick={handleRemove}
            disabled={open?.type === "refresh"}
            className=" bg-transparent hover:bg-transparent shadow-none text-peach hover:text-peach-dark"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="text-right text-sm text-gray-600">
          <div>Net Total:</div>
          <div className="text-lg font-bold text-black">
            <FormattedNumber
              value={calculateNetTotal(
                currentQuantity &&
                  selected === "sales" &&
                  data?.Sales_Qunatity !== null &&
                  data?.Sales_Qunatity !== 0 &&
                  data?.Sales_Price !== null &&
                  Number(data?.Sales_Price) !== 0
                  ? String(data?.Sales_Price)
                  : String(redixData?.NDP_price),
                data?.GST,
                currentQuantity
              )}
            />
          </div>
        </div>
      </div>

      {selected === "sales" && (
        <p className="text-[11px] text-gray-500 text-center mt-2">
          <strong>Note:</strong> Increase as pack of {data?.Bundle} items
        </p>
      )}
    </Card>
  );
};

export default MobileProductCard;
